import React, { useMemo } from 'react';

import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useGetMappedStatusEvent } from '@api/emp/queryHooks';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import { createFiltersGroup } from '@components/mapping-panel/utils/createFiltersUtils';
import { mappedEventDimensionType } from '@src/common/constant/enum';
import { AutoComplete as AutoCompleteStyled } from '../styled';
import { ISelect } from '@src/emp-3.0/components/common/AntdEnhancement';
import { calcShortcutIntervalWithinBoundary } from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import { EStringOp, ESystemType } from '@src/emp-3.0/types';
import { useGetDataDictionary } from '@src/emp-3.0/api/queryHooks';
import { buildQueryPayload } from './util';

const TagKeyAutoComplete: React.FC<
  { rawEventName: string; rule: any; src?: string; mappingRules?: any } & ISelect
> = ({ rawEventName, rule, src, mappingRules, ...rest }) => {
  const isTagValueNonContainsOperator =
    rule.op !== EStringOp.CONTAINS && rule.op !== EStringOp.NOT_CONTAINS;
  const [from, to] = calcShortcutIntervalWithinBoundary(DATE_SHORTCUTS.last7Days, ESystemType.EMP);

  // Conditionally call useQueryDimensionData only when src is NOT 'event_subject'
  const { data: tagKeyData, isLoading } = useQueryDimensionData(
    {
      fields: [mappedEventDimensionType.merged_event_tag_key],
      interval: `${from.format()}/${to.format()}`,
      filters: isTagValueNonContainsOperator
        ? createFiltersGroup(rawEventName, undefined, rule.tag_value.value[0])
        : createFiltersGroup(rawEventName),
      options: {
        enabled: src !== ESystemType.EventSubject,
      },
    },
    500,
  );

  const tagKeyPayload = useMemo(
    () => buildQueryPayload(mappedEventDimensionType.merged_event_tag_key, rule, mappingRules),
    [rule?.tag_value?.value[0]],
  );

  // Conditionally call useGetDataDictionary only when src is 'event_subject'
  const { data: tagKeyDataEventSubject, isFetching: isHostLoading } = useGetDataDictionary(
    tagKeyPayload,
    mappedEventDimensionType.merged_event_tag_key,
    src === ESystemType.EventSubject,
  );

  const { data: mappedTagKeyData } = useGetMappedStatusEvent('tag_key');

  // Use the appropriate data source based on src
  const finalTagKeyData = src === ESystemType.EventSubject ? tagKeyDataEventSubject : tagKeyData;
  const finalIsLoading = src === ESystemType.EventSubject ? isHostLoading : isLoading;

  const tagKeyOptions = useMemo(() => {
    return ((finalTagKeyData as any[]) ?? []).map((item) => ({
      label: item[mappedEventDimensionType.merged_event_tag_key],
      value: item[mappedEventDimensionType.merged_event_tag_key],
    }));
  }, [finalTagKeyData]);

  const Suffix = useMemo(() => {
    if (finalIsLoading) return <LoadingOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />;
    return <DownOutlined />;
  }, [finalIsLoading]);

  return (
    <AutoCompleteStyled
      value={rule.tag_key.value[0] || undefined}
      options={tagKeyOptions}
      mappedData={mappedTagKeyData?.data}
      suffixIcon={Suffix}
      style={{ width: '100%' }}
      {...rest}
    />
  );
};

export default TagKeyAutoComplete;
